/**
 *  @swagger
 *  definitions:
 *    RegionParams:
 *      type: object
 *      properties:
 *        Name:
 *          description: Name of the Region
 *          type: string
 *        Domain:
 *          description: Domain of the Region
 *          type: string
 *        RegistrationLetters:
 *          description: Registration Letters of the Region
 *          type: string
 *        Timezone:
 *          description: Timezone of the Region
 *          type: string
 *        ShortDateFormat:
 *          description: Short Date Format of the Region
 *          type: string
 *        DateFormat:
 *          description: Date Format of the Region
 *          type: string
 *        TimeFormat:
 *          description: Time Format of the Region
 *          type: string
 *        Flag:
 *          description: Flag of the Region
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegionCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionParams'
 *        - type: object
 *          required:
 *            - Name
 *            - Domain
 *            - RegistrationLetters
 *            - Timezone
 *            - ShortDateFormat
 *            - DateFormat
 *            - TimeFormat
 *            - Flag
 *
 *  @swagger
 *  definitions:
 *    RegionQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Region
 *              type: string
 *        - $ref: '#/definitions/RegionParams'
 *
 *  @swagger
 *  definitions:
 *    RegionUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Region:
 *      allOf:
 *        - $ref: '#/definitions/RegionCreateParams'
 *        - $ref: '#/definitions/RegionUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Region:
 *        allOf:
 *          - $ref: '#/definitions/Region'
 *          - type: object
 *            properties:
 *              RegionSeasons:
 *                description: RegionSeasons of the Region
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegionSeason'
 *              Entries:
 *                description: Entries of the Region
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Entry'
 *              CustomPages:
 *                description: CustomPages of the Region
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/CustomPage'
 *              Ratings:
 *                description: Ratings of the Region
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Rating'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  DateFormat: string;
  Domain: string;
  Flag: string;
  Name: string;
  RegistrationLetters: string;
  ShortDateFormat: string;
  TimeFormat: string;
  Timezone: string;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  CustomPages?: Array<$Model<'CustomPage'>>;
  Entries?: Array<$Model<'Entry'>>;
  Ratings?: Array<$Model<'Rating'>>;
  RegionSeasons?: Array<$Model<'RegionSeason'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  DateFormat: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Domain: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Flag: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RegistrationLetters: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ShortDateFormat: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  TimeFormat: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Timezone: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Region'> => {
  const Region = stack.define(
    'Region',
    fields(Sequelize),
    {
      tableName: 'Regions',
    },
  );

  Region.associate = (models: $ModelClasses) => {
    models.Region.hasMany(
      models.RegionSeason,
      {
        as: 'RegionSeasons',
        foreignKey: 'RegionId',
      },
    );
    models.Region.hasMany(
      models.Entry,
      {
        as: 'Entries',
        foreignKey: 'RegionId',
      },
    );
    models.Region.hasMany(
      models.CustomPage,
      {
        as: 'CustomPages',
        foreignKey: 'RegionId',
      },
    );
    models.Region.hasMany(
      models.Rating,
      {
        as: 'Ratings',
        foreignKey: 'RegionId',
      },
    );
  };

  Region.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Region;
});
